import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useUser } from "../../contexts/UserContext";
import { useCurrentContext } from "../../hooks/useContext";
import { getApiUrl } from "../../config/api";

import { Badge } from "../ui/badge";
import {
  ChevronDown,
  ChevronRight,
  Building2,
  Users,
  Shield,
  BarChart3,
  Settings,
  User,
  Briefcase,
  ClipboardList,
  AlertTriangle,
  FileText,
  ArrowLeft,
  Home,
  HelpCircle,
  LogOut,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";

const Sidebar = () => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const {
    primaryRole,
    userProfile,
    isOrgAdmin,
    isBusinessUnitHead,
    isBusinessUser,
    canManageOrganization,
  } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentContext, isOrganizationContext, isBusinessUnitContext } =
    useCurrentContext();

  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);

  const [expandedSections, setExpandedSections] = useState({
    entity: true,
    riskManagement: true,
    reports: true,
    administration: false,
  });

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleProfileClick = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleProfileMenuClick = () => {
    setIsProfileDropdownOpen(false);
    navigate("/profile");
  };

  const fetchOrganizations = async () => {
    if (!isOrgAdmin) return;

    try {
      setOrganizationsLoading(true);
      const response = await fetch(getApiUrl("organizations"), {
        headers: {
          Authorization: `Bearer ${await getAccessTokenSilently()}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch organizations");
      }

      const data = await response.json();
      setOrganizations(data);
    } catch (err) {
      console.error("Error fetching organizations:", err);
      setOrganizations([]);
    } finally {
      setOrganizationsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, [isOrgAdmin, getAccessTokenSilently]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleOrganizationSelect = (organization) => {
    if (organization.approved) {
      navigate(`/organization/${organization.id}/dashboard`);
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case "org_admin":
        return "Organization Admin";
      case "business_unit_head":
        return "Business Unit Head";
      case "business_user":
        return "Business User";
      default:
        return "No Role";
    }
  };

  const getRoleVariant = (role) => {
    switch (role) {
      case "org_admin":
        return "destructive";
      case "business_unit_head":
        return "default";
      case "business_user":
        return "secondary";
      default:
        return "outline";
    }
  };

  const isActiveLink = (path) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const isOrganizationsSectionActive = () => {
    return (
      location.pathname === "/organizations" ||
      location.pathname.startsWith("/organizations/") ||
      location.pathname.startsWith("/organization/")
    );
  };

  const NavLink = ({ to, icon: Icon, children, badge, isActive }) => (
    <Link
      to={to}
      className={`relative flex items-center gap-3 px-4 py-3 rounded-md text-sm font-semibold transition-colors ${
        isActive
          ? "bg-gray-100 text-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-muted"
      }`}
    >
      {/* Left tick indicator for active state */}
      {isActive && (
        <div className="absolute -left-5 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-primary rounded-r-sm"></div>
      )}

      <Icon
        className={`h-5 w-5 flex-shrink-0 ${
          isActive ? "text-primary" : "text-gray-500"
        }`}
      />
      <span className="flex-1">{children}</span>
      {badge && (
        <Badge variant="secondary" className="text-body-small">
          {badge}
        </Badge>
      )}
      {/* Right arrow for active state */}
      {isActive && (
        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
      )}
    </Link>
  );

  const SectionHeader = ({
    icon: Icon,
    title,
    section,
    children,
    isActive,
  }) => (
    <div className="space-y-1">
      <button
        onClick={() => toggleSection(section)}
        className={`relative flex items-center gap-3 px-4 py-3 w-full text-left text-sm font-bold rounded-md transition-colors ${
          isActive
            ? "bg-gray-100 text-foreground"
            : "text-foreground hover:bg-muted"
        }`}
      >
        {/* Left tick indicator for active state */}
        {isActive && (
          <div className="absolute -left-5 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-primary rounded-r-sm"></div>
        )}

        <Icon
          className={`h-5 w-5 flex-shrink-0 ${
            isActive ? "text-primary" : "text-gray-500"
          }`}
        />
        <span className="flex-1">{title}</span>
        {expandedSections[section] ? (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {expandedSections[section] && (
        <div className="pl-7 space-y-1">{children}</div>
      )}
    </div>
  );

  // Context-specific navigation
  const getContextualNavigation = () => {
    const { type, id, name } = currentContext;

    if (isOrganizationContext && id) {
      return (
        <>
          {/* Back to Global Navigation */}
          <div className="space-y-1">
            <NavLink to="/organizations" icon={ArrowLeft} isActive={false}>
              Back to Organizations
            </NavLink>
          </div>

          {/* Organization Dashboard */}
          <div className="space-y-1">
            <NavLink
              to={`/organization/${id}/dashboard`}
              icon={Home}
              isActive={isActiveLink(`/organization/${id}/dashboard`)}
            >
              Dashboard
            </NavLink>
          </div>

          {/* Single-level Organization Navigation */}
          <div className="space-y-1">
            <NavLink
              to={`/organization/${id}/business-units`}
              icon={Building2}
              isActive={isActiveLink(`/organization/${id}/business-units`)}
            >
              Business Units
            </NavLink>
            {isOrgAdmin && (
              <NavLink
                to={`/organization/${id}/users`}
                icon={Users}
                isActive={isActiveLink(`/organization/${id}/users`)}
              >
                Users & Access
              </NavLink>
            )}
            <NavLink
              to={`/organization/${id}/risks`}
              icon={AlertTriangle}
              isActive={isActiveLink(`/organization/${id}/risks`)}
            >
              Risks
            </NavLink>
            <NavLink
              to={`/organization/${id}/controls`}
              icon={Shield}
              isActive={isActiveLink(`/organization/${id}/controls`)}
            >
              Controls
            </NavLink>
            {isOrgAdmin && (
              <NavLink
                to={`/organization/${id}/settings`}
                icon={Settings}
                isActive={isActiveLink(`/organization/${id}/settings`)}
              >
                Metadata
              </NavLink>
            )}
          </div>
        </>
      );
    }

    if (isBusinessUnitContext && id) {
      // Get the organization ID from the business unit data if available
      const organizationId = currentContext.data?.organization_id;

      return (
        <>
          {/* Back Navigation */}
          <div className="space-y-1">
            {/* If user has org access and we have org ID, show org navigation */}
            {organizationId &&
            (isOrgAdmin || canManageOrganization(organizationId)) ? (
              <>
                <NavLink
                  to={`/organization/${organizationId}/dashboard`}
                  icon={Building2}
                  isActive={false}
                >
                  Go to Organization
                </NavLink>
                <NavLink
                  to={`/organization/${organizationId}/business-units`}
                  icon={ArrowLeft}
                  isActive={false}
                >
                  Back to Business Units
                </NavLink>
              </>
            ) : (
              <NavLink to="/business-units" icon={ArrowLeft} isActive={false}>
                Back to Business Units
              </NavLink>
            )}
          </div>

          {/* Business Unit Dashboard */}
          <div className="space-y-1">
            <NavLink
              to={`/business-unit/${id}/dashboard`}
              icon={Home}
              isActive={isActiveLink(`/business-unit/${id}/dashboard`)}
            >
              Dashboard
            </NavLink>
          </div>

          {/* Single-level Business Unit Navigation */}
          <div className="space-y-1">
            {(isOrgAdmin || isBusinessUnitHead) && (
              <NavLink
                to={`/business-unit/${id}/users`}
                icon={Users}
                isActive={isActiveLink(`/business-unit/${id}/users`)}
              >
                Users & Access
              </NavLink>
            )}
            <NavLink
              to={`/business-unit/${id}/risks`}
              icon={AlertTriangle}
              isActive={isActiveLink(`/business-unit/${id}/risks`)}
            >
              Risks
            </NavLink>
            <NavLink
              to={`/business-unit/${id}/controls`}
              icon={Shield}
              isActive={isActiveLink(`/business-unit/${id}/controls`)}
            >
              Controls
            </NavLink>
          </div>
        </>
      );
    }

    // Global navigation (default) - show all possible options
    return (
      <>
        {/* Organization Management */}
        {isOrgAdmin && (
          <div className="space-y-1">
            <NavLink
              to="/organizations"
              icon={Building2}
              isActive={isOrganizationsSectionActive()}
            >
              Organizations
            </NavLink>
            {organizationsLoading ? (
              <div className="px-4 py-2 text-sm text-muted-foreground pl-7">
                Loading organizations...
              </div>
            ) : (
              <div className="pl-7 space-y-1">
                {organizations.map((organization) => (
                  <button
                    key={organization.id}
                    onClick={() => handleOrganizationSelect(organization)}
                    disabled={!organization.approved}
                    className={`relative flex items-center gap-3 px-4 py-3 rounded-md text-sm font-semibold transition-colors w-full text-left ${
                      isActiveLink(`/organization/${organization.id}`)
                        ? "bg-gray-100 text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    } ${
                      !organization.approved
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {/* Left tick indicator for active state */}
                    {isActiveLink(`/organization/${organization.id}`) && (
                      <div className="absolute -left-5 top-1/2 transform -translate-y-1/2 w-1 h-6 bg-primary rounded-r-sm"></div>
                    )}

                    <Building2
                      className={`h-5 w-5 flex-shrink-0 ${
                        isActiveLink(`/organization/${organization.id}`)
                          ? "text-primary"
                          : "text-gray-500"
                      }`}
                    />
                    <span className="flex-1">{organization.name}</span>
                    {!organization.approved && (
                      <Badge variant="secondary" className="text-xs">
                        Pending
                      </Badge>
                    )}
                    {/* Right arrow for active state */}
                    {isActiveLink(`/organization/${organization.id}`) && (
                      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Context Selection Guidance */}
        {!isOrgAdmin && !isBusinessUnitHead && !isBusinessUser && (
          <div className="px-4 py-5 text-center">
            <div className="text-base text-muted-foreground">
              Select an organization to access management features
            </div>
          </div>
        )}
      </>
    );
  };

  const handleLogoClick = () => {
    const { type, id } = currentContext;

    if (isOrganizationContext && id) {
      navigate(`/organization/${id}/dashboard`);
    } else if (isBusinessUnitContext && id) {
      navigate(`/business-unit/${id}/dashboard`);
    } else {
      // For global context, navigate to dashboard or appropriate landing page
      switch (primaryRole) {
        case "org_admin":
          navigate("/organizations");
          break;
        case "business_unit_head":
          navigate("/business-units");
          break;
        case "business_user":
          navigate("/business-units");
          break;
        default:
          navigate("/");
          break;
      }
    }
  };

  // Show sidebar for all contexts, including global context

  return (
    <div className="bg-sidebar border-r border-border h-screen flex flex-col w-96">
      {/* Header */}
      <div className="p-5">
        <div className="border-b border-border pb-5">
          <button
            onClick={handleLogoClick}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <img
              src={
                process.env.PUBLIC_URL +
                "/assets/logos/black_on_white_full_4.png"
              }
              alt="Zentra Logo"
              className="w-48 h-auto max-h-14 object-contain"
            />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-5 space-y-2 overflow-y-auto">
        {getContextualNavigation()}
      </nav>

      {/* Footer */}
      <div className="px-5 pb-2">
        <NavLink to="/help" icon={HelpCircle} isActive={isActiveLink("/help")}>
          Need help?
        </NavLink>
      </div>

      {/* Profile Section */}
      <div className="px-5 pb-5">
        <div className="border-t border-border pt-5">
          <div className="relative" ref={profileDropdownRef}>
            <button
              onClick={handleProfileClick}
              className="flex items-center gap-3 w-full p-3 rounded-md hover:bg-muted transition-colors"
            >
              {/* User Avatar */}
              <img
                src={user?.picture || "/default-avatar.svg"}
                alt={userProfile?.name || user?.name || "User"}
                className="w-10 h-10 rounded-full border border-border"
              />

              {/* User Info */}
              <div className="flex-1 text-left">
                <div className="font-semibold text-sm text-foreground">
                  {userProfile?.name || user?.name || "User"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {userProfile?.title || getRoleDisplayName(primaryRole)}
                </div>
              </div>

              {/* Dropdown Arrow */}
              <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
            </button>

            {/* Profile Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div className="absolute bottom-full left-0 mb-2 w-full bg-popover border border-border rounded-md shadow-lg z-50">
                <div className="py-1">
                  <button
                    onClick={handleProfileMenuClick}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
                  >
                    <User className="h-4 w-4" />
                    Profile
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
